import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import './AssistantRegistrar.css';

// Finance details (can be moved to config if needed)
const FINANCE_NAME = 'SHREE SIDDHARAMESHWAR FINANCE AND LEASING';
const FINANCE_ADDRESS = 'Kasgi';
const MONEY_LENDING_SL_NO = '000';

// Convert number to words function (Indian numbering system)
function convertToWords(number) {
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (number === 0) return 'Zero';

  let num = Math.floor(number);
  let word = '';

  // Handle Crores
  if (num >= 10000000) {
    const crores = Math.floor(num / 10000000);
    word += convertHundred(crores) + ' Crore ';
    num %= 10000000;
  }

  // Handle Lakhs
  if (num >= 100000) {
    const lakhs = Math.floor(num / 100000);
    word += convertHundred(lakhs) + ' Lakh ';
    num %= 100000;
  }

  // Handle Thousands
  if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    word += convertHundred(thousands) + ' Thousand ';
    num %= 1000;
  }

  // Handle Hundreds, Tens and Units
  if (num > 0) {
    word += convertHundred(num);
  }

  return word.trim() + ' Rupees Only';

  function convertHundred(num) {
    let result = '';

    if (num > 99) {
      result += units[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }

    if (num > 19) {
      result += tens[Math.floor(num / 10)] + ' ';
      if (num % 10 > 0) {
        result += units[num % 10] + ' ';
      }
    } else if (num > 9) {
      result += teens[num - 10] + ' ';
    } else if (num > 0) {
      result += units[num] + ' ';
    }

    return result;
  }
}

// Generate month options from Sep 2025 to Sep 2026
function generateMonthOptions() {
  const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  const options = [];
  
  // Start from September 2025
  for (let i = 0; i < months.length; i++) {
    const year = i < 4 ? 2025 : 2026;
    options.push({
      value: `${months[i]}-${year}`,
      label: `${months[i]} ${year}`,
      month: months[i],
      year: year.toString()
    });
  }
  
  return options;
}

const AssistantRegistrar = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const monthOptions = generateMonthOptions();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMonth('');
  };

  const handleGeneratePDF = async () => {
    if (!selectedMonth) {
      alert('Please select a month');
      return;
    }

    const [month, year] = selectedMonth.split('-');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/assistant-registrar-form6/${month}/${year}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const data = await response.json();

      if (data.loans.length === 0) {
        alert('No data found for the selected month');
        setIsLoading(false);
        return;
      }

      // Generate PDF
      generatePDF(data, month, year);
      handleCloseDialog();
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error fetching data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = (data, month, year) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const currentDate = new Date();
    let yPos = 20;

    // Helper function to add centered text
    const addCenteredText = (text, fontSize, y) => {
      doc.setFontSize(fontSize);
      const textWidth = doc.getTextWidth(text);
      const x = (pageWidth - textWidth) / 2;
      doc.text(text, x, y);
    };

    // Helper function to determine loan type from account_no
    const getLoanType = (accountNo) => {
      if (!accountNo) return 'Unknown';
      const accountStr = accountNo.toString().toUpperCase();
      if (accountStr.startsWith('GL')) return 'General Loan';
      if (accountStr.startsWith('CD')) return 'CD Loan';
      if (accountStr.startsWith('TBD')) return 'TBD Loan';
      return 'Unknown';
    };

    // PAGE 1: Letter
    // Header (centered)
    addCenteredText(FINANCE_NAME, 16, yPos);
    yPos += 8;
    addCenteredText(FINANCE_ADDRESS, 12, yPos);
    yPos += 10;
    
    // Money Lending S.L. No.
    doc.setFontSize(12);
    doc.text(`Money Lending S.L. No.: ${MONEY_LENDING_SL_NO}`, 20, yPos);
    yPos += 7;
    
    // Date
    doc.text(`Date: ${formatDateToDDMMYYYY(currentDate.toISOString().split('T')[0])}`, 20, yPos);
    yPos += 10;
    
    // Line separator
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 15;

    // Letter content - properly aligned and justified
    doc.setFontSize(11);
    const leftMargin = 20;
    const rightMargin = pageWidth - 20;
    const textWidth = rightMargin - leftMargin;
    
    // To address
    doc.text('To,', leftMargin, yPos);
    yPos += 7;
    doc.text('The Assistant Registrar,', leftMargin, yPos);
    yPos += 7;
    doc.text('Money Lenders (C.S),', leftMargin, yPos);
    yPos += 7;
    doc.text('Gulbarga', leftMargin, yPos);
    yPos += 12;

    // Subject line
    doc.setFont('helvetica', 'bold');
    doc.text(`Subject: Submission of Form–6 for the month of ${month} ${year}`, leftMargin, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 12;

    // Letter body - justified text
    const letterBody = [
      `With reference to the above subject, we hereby submit Form–6 for the month of ${month} ${year}, along with the statement of loans advanced.`,
      `Total loan advanced: Rs. ${data.totalAmount.toFixed(2)}`,
      `(${convertToWords(data.totalAmount)})`,
      data.lowestFormNo !== null && data.highestFormNo !== null
        ? `Form–6 from Sl. No. ${data.lowestFormNo} to ${data.highestFormNo}, with a total of ${data.totalCount} entries only.`
        : `Form–6 with a total of ${data.totalCount} entries only.`,
      'Kindly accept the same.',
      'Thanking you,'
    ];

    letterBody.forEach((paragraph, index) => {
      // Split long text into lines that fit the page width
      const lines = doc.splitTextToSize(paragraph, textWidth);
      lines.forEach((line) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, leftMargin, yPos, { maxWidth: textWidth });
        yPos += 7;
      });
      // Add extra space after paragraphs
      if (index < letterBody.length - 1) {
        yPos += 3;
      }
    });

    // PAGE 2: Table
    doc.addPage();
    yPos = 20;

    // Header (centered)
    addCenteredText(FINANCE_NAME, 16, yPos);
    yPos += 8;
    addCenteredText(FINANCE_ADDRESS, 12, yPos);
    yPos += 10;
    doc.text(`Money Lending S.L. No.: ${MONEY_LENDING_SL_NO}`, 20, yPos);
    yPos += 10;
    
    // Date
    doc.text(`Date: ${formatDateToDDMMYYYY(currentDate.toISOString().split('T')[0])}`, 20, yPos);
    yPos += 15;

    // Prepare table data
    const tableData = data.loans.map((loan, index) => [
      index + 1, // Serial number
      loan.loanee_name || '-', // Name of the Party
      loan.form_no || '-', // Form no
      loan.account_no || '-', // Account no
      loan.loan_type || getLoanType(loan.account_no), // Loan Type (use backend value or derive from account_no)
      formatDateToDDMMYYYY(loan.date), // Date
      `Rs. ${parseFloat(loan.loan_amount || 0).toFixed(2)}` // Amount
    ]);

    // Add total row
    tableData.push([
      '',
      '',
      '',
      '',
      '',
      'Total Amount:',
      `Rs. ${data.totalAmount.toFixed(2)}`
    ]);

    // Generate table using autoTable
    // Calculate available width (page width - margins)
    const availableWidth = pageWidth - 40; // 20px margin on each side
    
    doc.autoTable({
      startY: yPos,
      head: [['Serial No.', 'Name of the Party', 'Form No', 'Account No', 'Loan Type', 'Date', 'Amount']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: false, 
        textColor: 0, 
        fontStyle: 'bold', 
        lineColor: 0, 
        lineWidth: 0.5 
      },
      styles: { 
        fontSize: 8, 
        cellPadding: 2, 
        lineColor: 0, 
        lineWidth: 0.5,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      columnStyles: {
        0: { cellWidth: availableWidth * 0.08, halign: 'center' }, // Serial No
        1: { cellWidth: availableWidth * 0.25 }, // Name
        2: { cellWidth: availableWidth * 0.10, halign: 'center' }, // Form No
        3: { cellWidth: availableWidth * 0.12, halign: 'center' }, // Account No
        4: { cellWidth: availableWidth * 0.15 }, // Loan Type
        5: { cellWidth: availableWidth * 0.15, halign: 'center' }, // Date
        6: { cellWidth: availableWidth * 0.15, halign: 'right' } // Amount
      },
      margin: { left: 20, right: 20 },
      tableWidth: 'wrap',
      showHead: 'everyPage',
      pageBreak: 'auto',
      overflow: 'linebreak',
      didDrawPage: function (data) {
        // Ensure table doesn't go beyond page boundaries
        if (data.cursor.y > pageHeight - 30) {
          return false; // Stop drawing if too close to bottom
        }
      }
    });

    // Add "End of statement" at the bottom
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    addCenteredText('End of statement', 11, finalY);

    // Save PDF
    const fileName = `Form-6_${month}_${year}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="assistant-registrar-container">
      <h2>Assistant Registrar</h2>
      
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenDialog}
          sx={{ minWidth: 200, py: 1.5 }}
        >
          Letter
        </Button>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select Month</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Month"
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleGeneratePDF} 
            variant="contained" 
            disabled={!selectedMonth || isLoading}
          >
            {isLoading ? 'Generating...' : 'Generate PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssistantRegistrar;

